import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../app/slices/EnvironmentSlice";
import {
  selectSelectedEnvironmentLayerIds,
  selectSelectedTake3D,
} from "../app/slices/TrainingSlice";
import {
  addEnvironmentPositionDisplayNames,
  getEnvironmentPositionContexts,
} from "../features/sequence/EnvironmentPositionContext";
import { filterTakePositionsByLayers } from "../features/viewport3d/environmentLayerVisibility";
import { ISetCamera } from "../models/interactions/SetCamera";
import useSituationHelper from "./SituationHelper.hook";

type CameraRef = Pick<ISetCamera, "cameraId" | "takeLocationId">;

/**
 * Every camera a Set Camera interaction can cut to, across take locations and situations,
 * limited to the take locations on visible map layers (like the map itself). `current`
 * stays listed even when its layer is hidden, so a chosen camera never reads as missing.
 */
export default function useSetCameraOptions(current?: CameraRef) {
  const currentTake = useSelector(selectSelectedTake3D);
  const { getSituationMetadata } = useSituationHelper();
  const ips = useSelector(selectEnvironmentInteractionPoints);
  const visibleLayerIds = useSelector(selectSelectedEnvironmentLayerIds);

  const all = useMemo(() => {
    const contexts = getEnvironmentPositionContexts(
      ips ?? [],
      currentTake?.situations,
      getSituationMetadata,
    );

    return addEnvironmentPositionDisplayNames(
      contexts.flatMap((context) =>
        context.cameraPositions.map((camera) => ({
          id: camera.id,
          sourceId: camera.id,
          name: camera.name,
          context,
        })),
      ),
    );
  }, [currentTake?.situations, getSituationMetadata, ips]);

  const currentTakeLocationLayer = (ips ?? []).find(
    (point) => point.id === currentTake?.interactionPointId,
  )?.layer;
  const currentCameraId = current?.cameraId;
  const currentTakeLocationId = current?.takeLocationId;

  return useMemo(() => {
    const visibleLocationIds = new Set(
      filterTakePositionsByLayers(ips ?? [], visibleLayerIds ?? [], currentTakeLocationLayer).map(
        (point) => point.id,
      ),
    );
    const kept = currentCameraId
      ? findSetCameraOption(all, {
          cameraId: currentCameraId,
          takeLocationId: currentTakeLocationId ?? "",
        })
      : undefined;
    return all.filter(
      (option) => visibleLocationIds.has(option.context.takePositionId) || option === kept,
    );
  }, [all, ips, visibleLayerIds, currentTakeLocationLayer, currentCameraId, currentTakeLocationId]);
}

type SetCameraOptionList = ReturnType<typeof addEnvironmentPositionDisplayNames>;
export type SetCameraOption = SetCameraOptionList[number];

/** Same camera id can exist at several take locations; prefer the one the interaction stored. */
export function findSetCameraOption(
  options: SetCameraOption[],
  setCamera: CameraRef,
) {
  return (
    options.find(
      (c) =>
        c.id === setCamera.cameraId &&
        (!setCamera.takeLocationId ||
          c.context.takeLocationId === setCamera.takeLocationId),
    ) ?? options.find((c) => c.id === setCamera.cameraId)
  );
}
